/**
 * Toasts - transient success/error feedback (docs/FRONTEND-PLAN.md section 2.1).
 *
 * Rule of thumb: every mutation that is NOT followed by visible new data
 * (a row that changes, a list that reloads) shows a toast, so the user is
 * never left wondering whether the click landed.
 *
 * `ToastHost` is mounted exactly once, in Layout. `useToast()` publishes into
 * it through a module-level subscription rather than a React context, so any
 * component in the authenticated tree can raise a toast without caring where
 * the host sits - including components rendered above it (Layout's own
 * header) or inside a portal (Modal). If no host is mounted the toast is
 * dropped silently, which is the correct behaviour on the login screens.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ApiError } from '../lib/apiClient';
import { cn } from '../lib/ui';

// Mobile-first: bottom-centered, full width; from the sm breakpoint up the
// stack pins to the bottom-right corner. z-300 keeps toasts above an open
// modal (z-200). The host is a full-width strip; only the toasts take clicks.
const HOST =
  'pointer-events-none fixed right-4 bottom-4 left-4 z-300 flex flex-col items-center gap-2 sm:left-auto sm:items-end';
const TOAST =
  'pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-dcon-md border px-4 py-3 text-body-s shadow-dcon-lg';
const TONES = {
  success: 'border-dcon-success bg-dcon-success-soft text-dcon-success',
  error: 'border-dcon-danger bg-dcon-danger-soft text-dcon-danger',
  info: 'border-dcon-info bg-dcon-info-soft text-dcon-info',
};

const AUTO_DISMISS_MS = 5000;

const subscribers = new Set();
let sequence = 0;

function toMessage(value) {
  if (value instanceof ApiError || value instanceof Error) return value.message;
  return String(value == null ? '' : value);
}

function publish(tone, message) {
  const toast = { id: `toast-${(sequence += 1)}`, tone, message: toMessage(message) };
  subscribers.forEach(notify => notify(toast));
}

/** `const toast = useToast(); toast.success('Saved'); toast.error(err); toast.info('Note');` */
export function useToast() {
  return useMemo(
    () => ({
      success: message => publish('success', message),
      error: message => publish('error', message),
      // Neutral notices (OrgSettings "Checkout cancelled", OrgSetupWizard
      // domain status) called toast.info before it existed and threw.
      info: message => publish('info', message),
    }),
    []
  );
}

export function ToastHost() {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef(new Map());

  const dismiss = useCallback(id => {
    const timers = timersRef.current;
    if (timers.has(id)) {
      clearTimeout(timers.get(id));
      timers.delete(id);
    }
    setToasts(current => current.filter(toast => toast.id !== id));
  }, []);

  useEffect(() => {
    const timers = timersRef.current;
    const receive = toast => {
      setToasts(current => [...current, toast]);
      timers.set(
        toast.id,
        setTimeout(() => {
          timers.delete(toast.id);
          setToasts(current => current.filter(item => item.id !== toast.id));
        }, AUTO_DISMISS_MS)
      );
    };
    subscribers.add(receive);
    return () => {
      subscribers.delete(receive);
      timers.forEach(timer => clearTimeout(timer));
      timers.clear();
    };
  }, []);

  return createPortal(
    <div className={HOST} aria-live="polite">
      {toasts.map(toast => (
        <div key={toast.id} className={cn(TOAST, TONES[toast.tone])}>
          <span className="flex-1">{toast.message}</span>
          <button
            type="button"
            className="cursor-pointer border-0 bg-transparent px-1 text-title-s leading-none text-current opacity-70 hover:opacity-100"
            aria-label="Dismiss message"
            onClick={() => dismiss(toast.id)}
          >
            &times;
          </button>
        </div>
      ))}
    </div>,
    document.body
  );
}

export default ToastHost;
